import { useEffect, useMemo, useState } from "react";
import api, { getWithRetry } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import TrackCard from "@/components/TrackCard";
import PurchaseModal from "@/components/PurchaseModal";
import CopyrightNotice from "@/components/CopyrightNotice";
import ReleaseCard from "@/components/ReleaseCard";
import DiscoveryBanner from "@/components/DiscoveryBanner";
import { Search } from "lucide-react";

export default function StorePage() {
  const { t } = useI18n();
  const [tracks, setTracks] = useState([]);
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("");
  const [country, setCountry] = useState("");
  const [releaseType, setReleaseType] = useState("");
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyTrack, setBuyTrack] = useState(null);
  const [projects, setProjects] = useState([]);
  // Distinct from "tracks is empty" — a failed request used to silently
  // leave the grid empty with zero indication anything went wrong,
  // indistinguishable from "no tracks match these filters" (same root
  // cause as the Feed page's identical bug).
  const [loadError, setLoadError] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (genre) params.genre = genre;
      if (country) params.country = country;
      if (releaseType) params.release_type = releaseType;
      const { data } = await getWithRetry("/tracks", { params });
      setTracks(data);
    } catch {
      setTracks([]);
      setLoadError(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { const tm = setTimeout(load, 200); return () => clearTimeout(tm); /* eslint-disable-next-line */ }, [q, genre, country, releaseType]);

  useEffect(() => {
    api.get("/releases", { params: { limit: 12 } }).then(({ data }) => setProjects(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/tracks/countries").then(({ data }) => setCountries(data)).catch(() => {});
  }, []);

  const genres = useMemo(() => Array.from(new Set(tracks.map((t) => t.genre).filter(Boolean))), [tracks]);
  const filtered = tracks;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6" data-testid="store-page">
      <DiscoveryBanner />
      <header className="anim-in mb-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[#00F2FE] font-bold">{t.store.title}</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black font-display text-white mt-1">{t.store.title}</h1>
        <p className="text-zinc-400 mt-2 max-w-xl">{t.store.sub}</p>
      </header>

      <div className="relative mb-3 anim-in">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.searchPh || "Titre ou artiste…"}
          className="input-base pl-9 w-full sm:max-w-sm"
          data-testid="store-search"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto scroll-hide mb-3 anim-in anim-delay-1">
        <FilterChip active={!genre} onClick={() => setGenre("")} label={t.store.filterAll} />
        {genres.map((g) => (
          <FilterChip key={g} active={genre === g} onClick={() => setGenre(g)} label={g} />
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["", "single", "ep", "album", "maxi"].map((rt) => (
          <FilterChip key={rt || "all"} active={releaseType === rt} onClick={() => setReleaseType(rt)} label={rt ? rt.toUpperCase() : (t.store.filterAll)} />
        ))}
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="input-base text-xs" data-testid="store-country" style={{ maxWidth: 200 }}>
          <option value="">Tous pays</option>
          {countries.map((c) => <option key={c.country} value={c.country}>{c.country} ({c.count})</option>)}
        </select>
      </div>

      {projects.length > 0 && !q.trim() && !genre && !country && (releaseType === "" || releaseType === "album" || releaseType === "ep" || releaseType === "maxi") && (
        <section className="mb-8" data-testid="store-projects">
          <h2 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Albums, EP et maxis</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {projects.filter((p) => !releaseType || p.release_type === releaseType).map((p) => <ReleaseCard key={p.id} release={p} />)}
          </div>
        </section>
      )}

      {loading ? (
        <p className="text-center text-zinc-500 py-12">{t.common.loading}</p>
      ) : loadError ? (
        <div className="text-center text-zinc-500 py-12" data-testid="store-load-error">
          <p>{t.store.loadError || "La boutique n'a pas pu charger — le serveur met parfois quelques secondes à répondre."}</p>
          <button onClick={load} className="btn-secondary mt-3 text-sm" data-testid="store-retry-btn">
            {t.store.retry || "Réessayer"}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="store-grid">
          {filtered.map((tr, i) => (
            <div key={tr.id} className="anim-in" style={{ animationDelay: `${i * 0.04}s` }}>
              <TrackCard track={tr} queue={filtered} onBuy={setBuyTrack} />
            </div>
          ))}
        </div>
      )}

      {buyTrack && (
        <PurchaseModal
          track={buyTrack}
          onClose={() => setBuyTrack(null)}
          onSuccess={() => load()}
        />
      )}

      <div className="mt-10 pt-6 border-t border-white/5">
        <CopyrightNotice />
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={`filter-${label}`}
      className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition ${active ? "bg-white text-black" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-white/5"}`}
    >{label}</button>
  );
}
